// Angular
import { ControlValueAccessor } from "@angular/forms";
// BCDFX - Globals
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Services
import { OELoggerService } from "../../services/basic/oe-logger.service";
import { ATDLUtilityService } from "../../services/atdl/atdl-utility.service";
import { ATDLComparatorService } from "../../services/atdl/atdl-comparator.service";
// Components
import { ICDBase } from "./icd-base.component";
// Models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDSpinnerModel } from "../../data-idc/controls/icd-spinner.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
import { ATDLNControlType } from "../../data-atdl/enums/atdl-control-type.enum";
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
// Enums
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { NumberSpinnerComponent, ZERO } from "shared/base-components/number-spinner.component";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
import { Injector } from "@angular/core";

/**
 * Base component class for the 4 ATDL spinner components - only needs to be overridden
 * for defining additional min/max constraints and precisions
 */
export abstract class ICDBaseSpinnerComponent
  extends ICDBase<ICDSpinnerModel>
  implements ControlValueAccessor
{
  /**********************************************************************/
  /* DATA */

  public config: {
    step: CubiqTS.BigNumber;
    minValue: CubiqTS.BigNumber;
    maxValue: CubiqTS.BigNumber;
    emptyValue: CubiqTS.BigNumber;
    minValueSet: boolean;
    maxValueSet: boolean;
    minMaxTouchValid: boolean;
    allowNegativeNum: boolean;
    precision: number;
  } = {
    step: new CubiqTS.BigNumber(1),
    minValue: null,
    maxValue: new CubiqTS.BigNumber(100),
    emptyValue: ZERO,
    minValueSet: false,
    maxValueSet: true,
    minMaxTouchValid: true,
    allowNegativeNum: false,
    precision: 2,
  };

  /**
   * Local data specific to this ICD component type
   */
  public subData: {
    numValue: CubiqTS.BigNumber;
    initValue: CubiqTS.BigNumber;
    defaultValue: CubiqTS.BigNumber;
  } = {
    numValue: null,
    initValue: null,
    defaultValue: null,
  };

  // CONSTRAINTS

  /************************************/
  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;
  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  /************************************/
  // @ViewChild("number_spinner", { static: true }) numberSpinner: NumberSpinnerComponent;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.resetIC();
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API */

  protected initializeICDSpecificModelProperties(): boolean {
    let error: boolean = false;
    this.cd.markForCheck();
    if (this.model instanceof ATDLInterfaceControlModel) {
      const allowedTypes = this.getAllowedTypes();
      // PARAMETER TYPE CHECK WARNING

      if (!allowedTypes.includes(this.model.paramType)) {
        logger.out.warn(
          `WARNING: ICD [${this.icdId}->${this.constructor.name}] with type '${
            ATDLNControlType[this.model.icdType]
          }' has incompatible parameter type of '${ATDLNParameterType[this.model.paramType]}' `,
        );
      }

      this.initConfigPrecision();

      // NEGATIVE NUMBERS
      this.initConfigNegative();

      // LOCAL VALIDATION
      if (this.model.validity != null) {
        // maxValue
        this.initConfigMaxValue();
        // minValue
        this.initConfigMinValue();
      }
      if (!this.config.maxValueSet) {
        this.config.maxValue = null;
      }
      if (!this.config.minValueSet) {
        this.config.minValue = null;
      }

      // INIT VALUE
      try {
        if (typeof this.model.initValue === "string" && this.model.initValue.length > 0) {
          this.subData.initValue = new CubiqTS.BigNumber(this.model.initValue);
          this.subData.defaultValue = new CubiqTS.BigNumber(this.model.initValue);
          this.setAndUpdateValue(this.subData.initValue);
        } else if (
          typeof this.model.defaultValue === "string" &&
          this.model.defaultValue.length > 0
        ) {
          this.subData.initValue = new CubiqTS.BigNumber(this.model.defaultValue);
          this.subData.defaultValue = new CubiqTS.BigNumber(this.model.defaultValue);
          this.setAndUpdateValue(this.subData.initValue);
        } else {
          this.subData.defaultValue = this.config.emptyValue;
          this.setAndUpdateValue(this.config.emptyValue);
        }
      } catch (e) {
        logger.out.error("Eorror initialising spinner value: " + e);
        this.subData.defaultValue = this.config.emptyValue;
        this.setAndUpdateValue(this.config.emptyValue);
      }
    } else {
      error = true;
    }
    return error;
  }

  protected initConfigPrecision() {
    if (this.model.paramType !== ATDLNParameterType.INT) {
      // PRECISION - only ignore when control is an INT
      if (typeof this.model.precision === "number" && this.model.precision <= 10) {
        this.cd.markForCheck();

        this.config.precision = this.model.precision;
      }
    }
  }

  protected initConfigNegative() {
    this.cd.markForCheck();

    if (
      [ATDLNParameterType.FLOAT, ATDLNParameterType.PRICE_OFFSET, ATDLNParameterType.INT].includes(
        this.model.paramType,
      )
    ) {
      this.config.allowNegativeNum = true;
    } else {
      this.config.allowNegativeNum = false;
    }
  }

  protected initConfigMaxValue() {
    this.cd.markForCheck();

    if (
      this.model.validity.hasOwnProperty("maxValue") &&
      typeof this.model.validity.maxValue === "number"
    ) {
      if (
        (this.config.allowNegativeNum && this.model.validity.maxValue < 0) ||
        this.model.validity.maxValue >= 0
      ) {
        this.config.maxValue = new CubiqTS.BigNumber(this.model.validity.maxValue);
        this.config.maxValueSet = true;
        // adjust empty value
        if (this.config.maxValue.isLessThan(0)) {
          this.config.emptyValue = this.config.maxValue;
        }
      } else {
        this.config.maxValue = null;
        this.config.maxValueSet = false;
      }
    }
  }

  protected initConfigMinValue() {
    this.cd.markForCheck();
    let minValue: CubiqTS.BigNumber | string | number = this.model.validity.minValue;
    let safeMinValue = true;

    try {
      let precision = this.config.precision != null ? Number(this.config.precision) : null;
      minValue = new CubiqTS.BigNumber(this.model.validity.minValue);

      if (
        precision > 0 &&
        minValue.isZero() == false &&
        minValue.lt(new CubiqTS.BigNumber("10").pow(-precision))
      ) {
        minValue = new CubiqTS.BigNumber("10").pow(-precision);
        safeMinValue = false;
        logger.out.info(
          `${this.constructor.name}.initConfigMinValue() adjusting` +
            ` minValue from ${this.model.validity.minValue} to ${minValue}` +
            ` due to configured precision ${precision}`,
        );
      }
    } catch (e) {
      logger.out.info(
        `${this.constructor.name}.initConfigMinValue() failed to adjust minValue to precision`,
      );
    }

    if (
      this.model.validity.hasOwnProperty("minValue") &&
      typeof this.model.validity.minValue === "number"
    ) {
      if (!this.config.allowNegativeNum && this.model.validity.minValue <= 0) {
        this.config.minValue = ZERO;
        this.config.minValueSet = true;
      } else {
        minValue = new CubiqTS.BigNumber(minValue);
        this.config.minValue =
          !this.config.maxValueSet ||
          (this.config.maxValueSet && !this.config.maxValue.isLessThan(minValue))
            ? minValue
            : ZERO;
        this.config.minValueSet =
          !this.config.maxValueSet ||
          (this.config.maxValueSet && !this.config.maxValue.isLessThan(minValue));
        // adjust empty value if non-zero and explicitly from ATDL
        if (safeMinValue && this.config.minValue.isGreaterThan(0)) {
          this.config.emptyValue = this.config.minValue;
        }
      }
    } else if (!this.config.allowNegativeNum) {
      this.config.minValueSet = true;
      this.config.minValue = ZERO;
    } else {
      this.config.minValueSet = false;
      this.config.minValue = null;
    }
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: ICDSpinnerModel): void {
    this.updateModel(value);
    this.numberSpinner.loadValue(this.subData.numValue);
  }

  public registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  public registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
    this.cd.markForCheck();
  }

  public onBlur(): void {
    this.inputFocused = false;
    // this.formatValues();
    this.updateNumValue(this.subData.numValue);
    this.validate();
    this.handleDynResponses(this.processAuxStateFlow(new Map()));
    this.cd.markForCheck();
  }

  public onStepperChange(): void {
    if (!this.numberSpinner) {
      return;
    }
    this.dirty = true;
    const newValue = this.numberSpinner.bigNumberValue;
    this.updateNumValue(newValue);
    this.validate();
  }

  public focusInput() {
    this.inputFocused = true;
    this.dirty = true;
    setTimeout(() => this.numberSpinner.focusInput(), 50);
  }

  protected setAndUpdateValue(value: any): void {
    let newValue: CubiqTS.BigNumber;
    if (value instanceof CubiqTS.BigNumber) {
      newValue = value;
    } else if (!isNaN(value) && value != null) {
      newValue = new CubiqTS.BigNumber(value);
    } else {
      newValue = this.config.emptyValue;
    }
    this.numberSpinner.loadValue(newValue);
    this.updateNumValue(newValue);
  }

  protected updateNumValue(value: CubiqTS.BigNumber): void {
    const model = this.compose(value);
    this.updateModel(model);
    this.onModelChange(model);
    this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
  }

  protected updateModel(value: ICDSpinnerModel): void {
    if (value instanceof ICDSpinnerModel) {
      this.data.value = value;
      this.model.value = value;
      this.subData.numValue = value.value;
      this.cd.markForCheck();
    } else {
      logger.out.error("writeValue NOT ICDRateSpinnerModel");
    }
  }

  /**********************************************************************/
  /* EXTERNAL API */
  public setDefault(): void {
    this.setAndUpdateValue(this.subData.defaultValue);
  }

  public updateValue(): void {
    if (
      this.subData.numValue == null ||
      (this.subData.numValue instanceof CubiqTS.BigNumber &&
        this.subData.numValue.isLessThan(0) &&
        !this.config.allowNegativeNum)
    ) {
      this.subData.numValue = this.config.emptyValue;
    }
    if (this.subData.numValue !== this.config.emptyValue) {
      // Remember last non-empty value
      this.subData.defaultValue = this.subData.numValue;
    }
    this.updateNumValue(this.subData.numValue);
    this.numberSpinner.loadValue(this.subData.numValue);
  }

  public load(value: ICDSpinnerModel, propagate?: boolean): void {
    let dynResponses = new Map();

    if (value instanceof ICDSpinnerModel || value === null) {
      this.cd.markForCheck();

      this.set(value);
      this.updateValue();
      this.validate();

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);
      dynResponses = this.processAuxStateFlow(dynResponses);

      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }

      this.handleDynResponses(dynResponses);
    }
  }

  /**
   *  Update the checkbox and subData properties from the input data model
   * @param value
   */
  public set(value: ICDSpinnerModel | CubiqTS.BigNumber): void {
    if (value instanceof ICDSpinnerModel || value instanceof CubiqTS.BigNumber || value == null) {
      this.dirty = true;
      this.cd.markForCheck();

      // full model with aux
      if (value instanceof ICDSpinnerModel && value.auxModel instanceof ICDAuxCheckboxModel) {
        if (value.auxModel.enabled) {
          this.auxCheckbox.enabled = value.auxModel.enabled;
          this.auxCheckbox.mode = value.auxModel.mode;
          this.data.disabled =
            typeof value.disabled === "boolean" ? value.disabled : this.data.disabled;
          if (value.auxModel.mode === ICDNAuxMode.DISABLED) {
            this.subData.numValue = null;
            //
            this.auxCheckbox.value = false;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.REQUIRED) {
            this.subData.numValue = value.value;
            //
            this.auxCheckbox.value = true;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.OPTIONAL) {
            this.subData.numValue = value.value;
            //
            this.auxCheckbox.value = value.auxModel.value;
            this.auxCheckbox.locked = false;
          } else {
            logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
          }
        } else {
          this.subData.numValue = value.value;
          this.disabled = this.data.disabled =
            typeof value.disabled === "boolean" ? value.disabled : this.data.disabled;
          //
          this.auxCheckbox.enabled = false;
          this.auxCheckbox.mode = ICDNAuxMode.DISABLED;
          this.auxCheckbox.value = false;
          this.auxCheckbox.locked = true;
        }
        // full model without aux
      } else if (value instanceof ICDSpinnerModel && value.auxModel == null) {
        this.data.disabled =
          typeof value.disabled === "boolean" ? value.disabled : this.data.disabled;
        this.subData.numValue = value.value;
        // bigNumber value
      } else if (value instanceof CubiqTS.BigNumber) {
        ///
        this.subData.numValue = value;
        ///
      } else if (value == null) {
        this.subData.numValue = null;
      } else {
        logger.out.error(`ERROR:  ${this.constructor.name}.set(): Invalid value type`);
      }
    }
  }

  // Sending
  protected compose(numValue: CubiqTS.BigNumber): ICDSpinnerModel {
    let model = new ICDSpinnerModel();
    let auxModel = new ICDAuxCheckboxModel();

    if (this.auxCheckbox.enabled) {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      auxModel.value = this.auxCheckbox.value;
      if (this.auxCheckbox.mode === ICDNAuxMode.DISABLED) {
        model.value = null;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.REQUIRED) {
        model.value = numValue;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.OPTIONAL) {
        model.value = this.auxCheckbox.value ? numValue : null;
      } else {
        logger.out.error(`ERROR: ICDRateSpinnerModel.set(): Invalid auxMode`);
        model.value = numValue;
      }
    } else {
      model.value = numValue;
      //
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = false;
      auxModel.mode = this.auxCheckbox.mode;
      auxModel.value = false;
    }
    if (model.value != null && this.numberSpinner != null) {
      model.textValue = this.numberSpinner.numericTextValue;
    } else {
      model.textValue = "";
    }
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.skipInitValue = this.data?.value?.skipInitValue;
    return model;
  }

  public validateValueWithDelay(offset: number = 0): void {
    setTimeout(() => {
      this.validate();
    }, offset);
  }

  public validateIC(): boolean {
    if (this.required || !(this.locked || (this.disabled && this.auxCheckbox.enabled == false))) {
      this.cd.markForCheck();

      if (this.inputFocused) {
        this.data.state = ICNInputControlState.ACTIVE;
        this.data.errorMsg = "";
        return true;
      } else {
        let isValid: boolean = true;

        if (this.required && !(this.subData.numValue instanceof CubiqTS.BigNumber)) {
          isValid = false;
          this.data.errorMsg = `${this.label} must have a value`;
        } else if (
          this.config.maxValueSet &&
          this.subData.numValue instanceof CubiqTS.BigNumber &&
          this.config.maxValue instanceof CubiqTS.BigNumber &&
          (this.config.minMaxTouchValid
            ? this.subData.numValue.isGreaterThan(this.config.maxValue)
            : this.subData.numValue.isGreaterThanOrEqualTo(this.config.maxValue))
        ) {
          isValid = false;
          this.data.errorMsg = `${
            this.label
          } greater than maximum allowed value of ${this.config.maxValue.toFixed(
            this.config.precision,
          )}`;
          this.cd.markForCheck();
        } else if (
          this.config.minValueSet &&
          this.subData.numValue instanceof CubiqTS.BigNumber &&
          this.config.minValue instanceof CubiqTS.BigNumber &&
          (this.config.minMaxTouchValid
            ? this.subData.numValue.isLessThan(this.config.minValue)
            : this.subData.numValue.isLessThanOrEqualTo(this.config.minValue))
        ) {
          isValid = false;
          this.data.errorMsg = `${
            this.label
          } less than minimum allowed value of ${this.config.minValue.toFixed(
            this.config.precision,
          )}`;
          this.cd.markForCheck();
        } else {
          isValid = this.additionalSpinnerValidation();
        }

        if (isValid) {
          this.data.state = ICNInputControlState.VALID;
          this.data.errorMsg = "";
          return true;
        } else {
          this.data.state = ICNInputControlState.INVALID;
          return false;
        }
      }
    } else {
      // checkbox is enabled but not checked
      this.cd.markForCheck();

      this.data.state = ICNInputControlState.INACTIVE;
      this.data.errorMsg = "";
      return true;
    }
  }

  /**
   * Perform any non-standard spinner validation in a subclass where the component is not focussed.
   * If this returns false the override fn should have aslo set the data.errorMsg to indicate what's wrong
   * @returns
   */
  protected additionalSpinnerValidation(): boolean {
    return true;
  }

  public resetIC(): void {
    this.cd.markForCheck();

    this.label = this.getDefaultLabel();
    this.initialiseConfiguration();

    this.subData.numValue = null;
    this.subData.initValue = null;
    this.subData.defaultValue = null;
  }

  protected abstract initialiseConfiguration(): void;

  protected abstract getDefaultLabel(): string;
  protected abstract getAllowedTypes(): ATDLNParameterType[];

  protected abstract get numberSpinner(): NumberSpinnerComponent;
}
